import React, {useState} from 'react'
import styled from 'styled-components'
import {useEvent} from 'Event/EventProvider'
import {api} from 'lib/url'
import {useOrganization} from 'organization/OrganizationProvider'
import {useForm} from 'react-hook-form'
import Page from 'lib/ui/layout/Page'
import Typography from '@material-ui/core/Typography'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import FormControl from '@material-ui/core/FormControl'
import Checkbox from '@material-ui/core/Checkbox'
import Button from '@material-ui/core/Button'
import {ZoomAreasBreadcrumbs} from 'organization/Event/Page/PageBreadcrumbs'
import {ErrorText} from 'lib/ui/TextField/HelperText'
import {useHistory} from 'react-router'
import {useEventRoutes} from 'organization/Event/EventRoutes'

export interface ZoomUserSettings {
  enable_reactions: boolean
  enable_recordings: boolean
  enable_transcripts: boolean
  enable_far_camera_control: boolean
  enable_optimize_recordings: boolean
  enable_recordings_participant_name: boolean
}

export default function UpdateZoomUserSettings() {
  const {register, handleSubmit} = useForm()
  const {event, set} = useEvent()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const saveZoomUserSettings = useSaveZoomUserSettings()
  const history = useHistory()
  const eventRoutes = useEventRoutes()

  const submit = (data: ZoomUserSettings) => {
    setSubmitting(true)
    setError('')
    saveZoomUserSettings(data)
      .then(() => {
        set({
          ...event,
          zoom_user_enable_reactions: data.enable_reactions,
          zoom_user_enable_recordings: data.enable_recordings,
          zoom_user_enable_transcripts: data.enable_transcripts,
          zoom_user_enable_far_camera_control: data.enable_far_camera_control,
          zoom_user_enable_optimize_recordings: data.enable_optimize_recordings,
          zoom_user_enable_recordings_participant_name:
            data.enable_recordings_participant_name,
        })
        history.push(eventRoutes.areas.root)
      })
      .catch((error) => {
        setError(error.message)
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  return (
    <ZoomAreasBreadcrumbs page="Create">
      <Page>
        <Title variant="h5" align="center">
          Zoom user settings
        </Title>
        <MutedNote>
          These settings will only apply after the room restarts.
        </MutedNote>
        <StyledForm onSubmit={handleSubmit(submit)}>
          <SubTitle>Main settings</SubTitle>

          <FieldsHolder>
            <StyledFieldset>
              <FormControlLabel
                inputRef={register}
                name="enable_reactions"
                aria-label="toggle reactions"
                control={
                  <Checkbox defaultChecked={event.zoom_user_enable_reactions} />
                }
                label="Turn on reactions"
              />
              <Info>
                Allows participants to use emoji reactions (like 👍 or 👏)
                during the meeting. These appear on screen to give quick,
                non-verbal feedback.
              </Info>
            </StyledFieldset>

            <StyledFieldset>
              <FormControlLabel
                inputRef={register}
                name="enable_transcripts"
                aria-label="toggle transcripts"
                control={
                  <Checkbox
                    defaultChecked={event.zoom_user_enable_transcripts}
                  />
                }
                label="Turn on transcripts"
              />
              <Info>
                Automatically generates a text transcript of everything spoken
                during the meeting. This helps with note-taking, accessibility,
                and reviewing the meeting later.
              </Info>
            </StyledFieldset>

            <StyledFieldset>
              <FormControlLabel
                inputRef={register}
                name="enable_far_camera_control"
                aria-label="toggle far end camera controll"
                control={
                  <Checkbox
                    defaultChecked={event.zoom_user_enable_far_camera_control}
                  />
                }
                label="Far end camera controll"
              />
              <Info>
                Lets a participant remotely control another participant’s camera
                (if supported). Useful for training, demonstrations, or
                assisting remote users with camera adjustments.
              </Info>
            </StyledFieldset>
          </FieldsHolder>

          <SubTitle>Recordings settings</SubTitle>

          <FieldsHolder>
            <StyledFieldset>
              <FormControlLabel
                inputRef={register}
                name="enable_recordings"
                aria-label="toggle recordings"
                control={
                  <Checkbox
                    defaultChecked={event.zoom_user_enable_recordings}
                  />
                }
                label="Turn on local recordings"
              />
              <Info>
                Allows the host or permitted participants to record the meeting
                directly to their computer instead of the cloud.
              </Info>
            </StyledFieldset>

            <StyledFieldset>
              <FormControlLabel
                inputRef={register}
                name="enable_optimize_recordings"
                aria-label="toggle optimize recordings"
                control={
                  <Checkbox
                    defaultChecked={event.zoom_user_enable_optimize_recordings}
                  />
                }
                label="Optimize recording for 3rd party video editor"
              />
              <Info>
                Adjusts the recording format so it works better with
                video-editing software. This can improve compatibility and
                editing performance.
              </Info>
            </StyledFieldset>

            <StyledFieldset>
              <FormControlLabel
                inputRef={register}
                name="enable_recordings_participant_name"
                aria-label="toggle participant name display"
                control={
                  <Checkbox
                    defaultChecked={
                      event.zoom_user_enable_recordings_participant_name
                    }
                  />
                }
                label="Display participant name"
              />
              <Info>
                If enabled, Zoom will embed each participant’s name into the
                recorded video (on their video tile), so that when someone
                watches the recording later, they can see who is speaking.
              </Info>
            </StyledFieldset>
          </FieldsHolder>
          <Error>{error}</Error>
          <ButtonRow>
            <Button
              variant="contained"
              color="primary"
              disabled={submitting}
              type="submit"
              aria-label="save zoom user settings"
            >
              Save Settings
            </Button>
          </ButtonRow>
        </StyledForm>
      </Page>
    </ZoomAreasBreadcrumbs>
  )
}

function Error(props: {children: string | null}) {
  if (!props.children) {
    return null
  }

  return (
    <ErrorText color="error" style={{textAlign: 'center', display: 'block'}}>
      We could save your settings: {props.children}
    </ErrorText>
  )
}

function useSaveZoomUserSettings() {
  const {client} = useOrganization()
  const {event} = useEvent()
  const url = api(`/events/${event.id}/zoom_user_settings`)
  return (data: ZoomUserSettings) => client.put<ZoomUserSettings>(url, data)
}

const SubTitle = styled.div`
  font-size: 1.6rem;
  font-weight: bold;
  margin-left: ${(props) => `${props.theme.spacing[4]}`};
  margin-bottom: ${(props) => `${props.theme.spacing[4]}`};
`

const Info = styled.i`
  opacity: 0.7;
  font-size: 0.9rem;
`

const Title = styled(Typography)`
  margin-bottom: 12px;
`

const StyledForm = styled.form`
  & > div {
    width: 100%;
  }
`

const MutedNote = styled.i`
  display: block;
  text-align: center;
  opacity: 0.7;
  font-size: 0.9rem;
  margin-bottom: 32px;
`

const FieldsHolder = styled.div`
  margin-bottom: ${(props) => `${props.theme.spacing[4]}`};
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.2rem;

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }

  & > div {
    width: 100%;
  }
`

const StyledFieldset = styled(FormControl)`
  width: 100%;
  border-radius: 8px;
  padding: 0 20px;
  cursor: pointer;

  justify-content: center;

  &:hover {
    background: #f5f5f5;
  }
`

const ButtonRow = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: center;
`
